import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import { calculatePriceAfterDiscount } from 'components/Discount';
import { getDiscountedPrice } from 'components/ReferencePriceSection';
import { getBasketOtherSection, getBasketVehicles } from 'domains/basket/Basket.store';
import { getDiscount, getPrice } from 'domains/references';
import { getTireSetParam } from 'domains/tires/Tire.store';
import { ClientView, GarageView, SparePartsViewType } from 'domains/user';
import { Flex, MarginBox, Space, Text } from 'UI';
import { getData, hasData, textFormatter } from 'utils';

export const TireBasketPriceInfo = ({
  referenceNumber,
  vehicleKey,
  sparePartsView,
  clientDiscount,
}: {
  referenceNumber: string;
  vehicleKey?: string;
  sparePartsView: SparePartsViewType;
  clientDiscount: number | undefined;
}) => {
  const { t } = useTranslation();
  const vehicles = useSelector(getBasketVehicles);
  const price = useSelector((state: RootState) => getPrice(state, referenceNumber));
  const setParam = useSelector(getTireSetParam);
  const referenceDiscount = useSelector((state: RootState) => getDiscount(state, referenceNumber));
  const priceData = getData(price);
  const otherSection = useSelector(getBasketOtherSection);
  const referencesOfCurrentVeh = useMemo(
    () => vehicles.find((vehicle) => vehicle.vehicleDetail.vehicleKey === vehicleKey)?.references,
    [vehicleKey, vehicles],
  );
  const isApplicableOrInCurrVehCart = useCallback(() => !!vehicleKey, [vehicleKey]);

  const basketReferences = isApplicableOrInCurrVehCart() ? referencesOfCurrentVeh : otherSection?.references;
  const currentBasketReference = basketReferences?.find((ref) => ref.referenceNumber === referenceNumber);
  const refQuantity = currentBasketReference?.quantity;
  const priceCoefficient = refQuantity ?? setParam;

  if (!hasData(price) || !priceCoefficient) {
    return null;
  }

  return (
    <MarginBox mt={2}>
      <Flex direction={'row'}>
        <Space size={10} direction={'horizontal'}>
          <Text type="base" transform={'capitalize'}>
            {t('catalog.tires.reference_card.subtotal_in_basket', 'Subtotal ({{count}} items) :', {
              count: priceCoefficient,
            })}
          </Text>

          {sparePartsView === ClientView && (
            <Text type="base" bold>
              {textFormatter.formatCurrency(
                priceCoefficient *
                  getDiscountedPrice(Number(priceData?.clientView?.recommendedPriceVatExcluded), clientDiscount),
                priceData?.currency ?? '',
              )}
            </Text>
          )}
          {sparePartsView === GarageView && priceData?.garageView && (
            <Text type="base" bold>
              {textFormatter.formatCurrency(
                priceCoefficient * calculatePriceAfterDiscount(referenceDiscount, priceData, false),
                priceData.currency,
              )}
            </Text>
          )}
        </Space>
      </Flex>
    </MarginBox>
  );
};
